import React, { type ReactNode, useMemo } from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, DEFAULT_OG_IMAGE, Seo, SITE_URL } from "@shared/components";
import { BLOG_POSTS, type BlogPostConfig } from "@shared/config/blog-posts";
import { getCardTitle, SEO_PAGES } from "@shared/config/seo-pages";
import { ORGANIZATION_SCHEMA, PERSON_SCHEMA } from "@shared/config/seo-schemas";
import { ChevronDown } from "lucide-react";

interface BlogPostPageProps {
  post: BlogPostConfig;
}

interface Heading {
  id: string;
  title: string;
}

/** Сколько соседних статей показываем в блоке «Читайте также» */
const RELATED_POSTS_LIMIT = 3;

const slugifyHeading = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-zа-яё0-9]+/gi, "-")
    .replace(/^-+|-+$/g, "");

/** Достаёт текст заголовка из произвольного JSX-дерева */
const extractText = (node: ReactNode): string => {
  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractText).join("");
  }
  if (React.isValidElement<{ children?: ReactNode }>(node)) {
    return extractText(node.props.children);
  }
  return "";
};

/**
 * Оглавление собирается из h2 самой статьи: конфиги постов остаются обычным
 * JSX, а якоря проставляются на лету при рендере.
 */
const useArticleOutline = (content: ReactNode): { headings: Heading[]; nodes: ReactNode[] } =>
  useMemo(() => {
    const headings: Heading[] = [];
    const source =
      React.isValidElement<{ children?: ReactNode }>(content) && content.type === React.Fragment
        ? content.props.children
        : content;

    const nodes = React.Children.toArray(source).map((child, index) => {
      if (
        !React.isValidElement<{ children?: ReactNode; className?: string; id?: string }>(child) ||
        child.type !== "h2"
      ) {
        return child;
      }

      const title = extractText(child.props.children);
      const id = slugifyHeading(title) || `razdel-${index}`;
      headings.push({ id, title });

      return React.cloneElement(child, {
        id,
        className: `${child.props.className ?? ""} scroll-mt-24`.trim()
      });
    });

    return { headings, nodes };
  }, [content]);

const formatDate = (date: string): string => new Date(`${date}T00:00:00`).toLocaleDateString("ru-RU");

/** Точка-разделитель в строке метаданных */
const MetaDot: React.FC = () => <span className="h-[3px] w-[3px] shrink-0 rounded-full bg-muted-foreground/40" />;

/** Карточка призыва к действию: в сайдбаре на десктопе, под текстом на мобильном */
const CtaCard: React.FC<{ className?: string }> = ({ className }) => (
  <div className={`flex flex-col gap-4 rounded-lg border border-primary p-5 ${className ?? ""}`}>
    <div className="flex flex-col gap-1.5">
      <p className="text-[19px] font-semibold leading-snug tracking-[-0.01em] text-foreground lg:text-[15px]">
        Проверьте на своей записи
      </p>
      <p className="text-sm leading-relaxed text-muted-foreground lg:text-[13px]">
        150 минут за 99 ₽ на пробный период.
      </p>
    </div>
    <Link
      to="/"
      className="flex h-12 items-center justify-center rounded-md bg-primary text-[15px] font-medium text-primary-foreground transition-opacity hover:opacity-90 lg:h-10 lg:text-sm"
    >
      Загрузить файл
    </Link>
    <p className="text-xs leading-relaxed text-muted-foreground lg:text-[12px]">
      Через 7 дней — Базовый за 598 ₽/мес, отмена в личном кабинете.
    </p>
  </div>
);

/** Аккордеон частых вопросов внутри колонки текста */
const ArticleFaq: React.FC<{ items: BlogPostConfig["faq"] }> = ({ items }) => (
  <div className="flex flex-col">
    {items.map((item, index) => (
      <details
        key={item.question}
        className={`group border-t border-border ${index === items.length - 1 ? "border-b" : ""}`}
      >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-[18px] text-[15px] font-medium leading-snug text-foreground marker:content-none md:text-base [&::-webkit-details-marker]:hidden">
          {item.question}
          <ChevronDown className="h-[18px] w-[18px] shrink-0 text-muted-foreground/70 transition-transform group-open:rotate-180" />
        </summary>
        <p className="pb-5 text-[15px] leading-relaxed text-muted-foreground md:text-base">{item.answer}</p>
      </details>
    ))}
  </div>
);

export const BlogPostPage: React.FC<BlogPostPageProps> = ({ post }) => {
  const path = `/blog/${post.slug}`;
  const { headings, nodes } = useArticleOutline(post.content);

  // Соседи берём по кругу, чтобы ссылки распределялись по всем статьям
  const relatedPosts = useMemo(() => {
    const currentIndex = BLOG_POSTS.findIndex(item => item.slug === post.slug);
    if (currentIndex === -1) {
      return [];
    }
    return Array.from({ length: Math.min(RELATED_POSTS_LIMIT, BLOG_POSTS.length - 1) }, (_, offset) => {
      return BLOG_POSTS[(currentIndex + offset + 1) % BLOG_POSTS.length];
    });
  }, [post.slug]);

  const relatedPages = useMemo(
    () =>
      (post.relatedPaths ?? [])
        .map(relatedPath => SEO_PAGES.find(page => page.path === relatedPath))
        .filter((page): page is (typeof SEO_PAGES)[number] => Boolean(page)),
    [post.relatedPaths]
  );

  const articleSchema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.metaTitle,
    description: post.metaDescription,
    articleSection: post.category,
    // Темы статьи: `keywords` описывает сущность для поисковика, `about` связывает
    // её с сущностями. Оба поля читаются из JSON-LD, в отличие от meta keywords
    keywords: post.tags?.join(", "),
    about: post.tags?.map(tag => ({ "@type": "Thing", name: tag })),
    datePublished: post.datePublished,
    dateModified: post.dateModified ?? post.datePublished,
    inLanguage: "ru-RU",
    mainEntityOfPage: `${SITE_URL}${path}`,
    // Автор раскрыт на /about тем же @id — без него Search Console помечает Article неполным
    author: { "@id": `${SITE_URL}/about#person` },
    image: DEFAULT_OG_IMAGE,
    publisher: { "@id": `${SITE_URL}/#organization` }
  };

  const faqSchema: Record<string, unknown> | null =
    post.faq.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map(item => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer }
          }))
        }
      : null;

  const jsonLd = [
    ORGANIZATION_SCHEMA,
    PERSON_SCHEMA,
    articleSchema,
    ...(faqSchema ? [faqSchema] : []),
    ...(post.extraJsonLd ?? [])
  ];

  return (
    <div className="overflow-hidden">
      <Seo
        title={post.metaTitle}
        description={post.metaDescription}
        path={path}
        jsonLd={jsonLd}
        ogType="article"
        article={{
          publishedTime: `${post.datePublished}T00:00:00+03:00`,
          modifiedTime: `${post.dateModified ?? post.datePublished}T00:00:00+03:00`,
          section: post.category,
          tags: post.tags
        }}
      />
      <BreadcrumbSchema
        items={[{ name: "Главная", url: "/" }, { name: "Блог", url: "/blog" }, { name: post.metaTitle }]}
      />

      <section className="border-b border-border pb-5 pt-6 md:pb-10 md:pt-12">
        <div className="container">
          <div className="flex flex-col gap-3.5 md:gap-5">
            <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground/70 md:gap-2">
              <Link to="/" className="hover:text-foreground">
                Главная
              </Link>
              <span>/</span>
              <Link to="/blog" className="hover:text-foreground">
                Блог
              </Link>
              <span>/</span>
              <span className="text-muted-foreground">{post.category}</span>
            </div>

            <h1 className="max-w-[860px] text-[30px] font-bold leading-[1.15] tracking-[-0.03em] text-foreground md:text-[44px] md:leading-[1.12]">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-2.5 text-[13px] text-muted-foreground md:gap-3 md:text-sm">
              <span className="flex h-6 items-center rounded-full bg-muted px-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground md:h-[26px] md:text-xs">
                {post.category}
              </span>
              <span>{formatDate(post.datePublished)}</span>
              <MetaDot />
              <span>{post.readingMinutes} мин чтения</span>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {post.tags.map(tag => (
                  <span
                    key={tag}
                    className="rounded-md border border-border px-2.5 py-1 text-[12px] leading-none text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Мобильное оглавление: сворачивается в одну строку под шапкой статьи */}
      {headings.length > 0 && (
        <div className="container pt-5 lg:hidden">
          <details className="group rounded-lg border border-border">
            <summary className="flex h-[52px] cursor-pointer list-none items-center justify-between px-4 marker:content-none [&::-webkit-details-marker]:hidden">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Содержание
              </span>
              <ChevronDown className="h-[18px] w-[18px] shrink-0 text-muted-foreground/70 transition-transform group-open:rotate-180" />
            </summary>
            <div className="flex flex-col gap-3 px-4 pb-4 text-sm">
              {headings.map(heading => (
                <a key={heading.id} href={`#${heading.id}`} className="text-muted-foreground hover:text-foreground">
                  {heading.title}
                </a>
              ))}
            </div>
          </details>
        </div>
      )}

      <section className="pb-12 pt-6 md:pb-[72px] md:pt-12">
        <div className="container">
          <div className="grid items-start gap-8 lg:grid-cols-[260px_1fr] lg:gap-16">
            <aside className="hidden flex-col gap-5 lg:sticky lg:top-20 lg:flex">
              {headings.length > 0 && (
                <nav className="flex flex-col gap-3.5 rounded-lg border border-border p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Содержание</p>
                  <div className="flex flex-col gap-2.5 text-sm leading-snug">
                    {headings.map(heading => (
                      <a
                        key={heading.id}
                        href={`#${heading.id}`}
                        className="text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {heading.title}
                      </a>
                    ))}
                  </div>
                </nav>
              )}
              <CtaCard />
            </aside>

            <div className="flex max-w-[720px] flex-col gap-6">
              <p className="text-[17px] leading-relaxed text-muted-foreground md:text-[19px]">{post.excerpt}</p>

              <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none space-y-4">{nodes}</div>

              {post.faq.length > 0 && (
                <div className="flex flex-col gap-6 pt-2">
                  <h2 className="text-[21px] font-semibold leading-tight tracking-[-0.02em] text-foreground md:text-2xl">
                    Частые вопросы
                  </h2>
                  <ArticleFaq items={post.faq} />
                </div>
              )}

              <CtaCard className="mt-2 lg:hidden" />
            </div>
          </div>
        </div>
      </section>

      {relatedPosts.length > 0 && (
        <section className="border-t border-border pb-10 pt-8 md:pb-20 md:pt-14">
          <div className="container">
            <div className="flex flex-col gap-4 md:gap-6">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">Читайте также</p>
              <div className="grid gap-3 md:grid-cols-3 md:gap-6">
                {relatedPosts.map(related => (
                  <Link
                    key={related.slug}
                    to={`/blog/${related.slug}`}
                    className="flex flex-col gap-2 rounded-lg border border-border p-5 transition-colors hover:border-primary md:gap-2.5 md:p-6"
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground md:text-xs">
                      {related.category} · {related.readingMinutes} мин
                    </span>
                    <span className="text-[17px] font-semibold leading-[1.3] tracking-[-0.015em] text-foreground md:text-lg">
                      {related.title}
                    </span>
                  </Link>
                ))}
              </div>

              {relatedPages.length > 0 && (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 pt-2 text-sm text-muted-foreground">
                  <span className="text-[13px]">Ещё по теме:</span>
                  {relatedPages.map((page, index) => (
                    <React.Fragment key={page.path}>
                      <Link to={page.path} className="text-[13px] text-foreground underline-offset-4 hover:underline">
                        {getCardTitle(page)}
                      </Link>
                      {index < relatedPages.length - 1 && (
                        <span className="text-[13px] text-muted-foreground/70">·</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};
